import React, { useRef, useState, useCallback, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";

// =============================================================================
// Tooltip — generic INSTANT hover tooltip (portal, SSOT surface)
// =============================================================================
// The app's canonical lightweight tooltip: appears the moment the cursor enters
// the trigger (NO hover delay — native `title` lags ~1s, which Mako rejected),
// portalled to <body> so it escapes the overflow:hidden of liquid-glass ancestors
// + scroll containers.
//
// LOOK comes from the Tooltip SSOT classes in index.css — `.tt-surface` (glass,
// default) or `.tt-surface-chart` (dark, over data-viz), backed by --tt-* tokens.
// This component never hardcodes colors; edit index.css to restyle it everywhere.
//
// Hardened: measures the real card after mount (correct flip on long content),
// Escape-to-close, a hoverable bridge (cursor can travel into the panel and it
// stays open), reposition-on-scroll, opt-in keyboard focus, opacity-only entrance
// (NO transform — backdrop-filter + transform blurs text ~0.4s).
//
// This is NOT MetricTooltip (METRIC_META + live-samples coupled) and NOT
// HoverTooltip (CSS group-hover, non-portal). Use this for any plain text/JSX
// hover hint that must show instantly and may live inside a clipping container.
// =============================================================================

interface TooltipProps {
  /** Tooltip body — plain string or JSX. Null/empty renders NO tooltip. */
  content: React.ReactNode;
  /** The trigger element(s) the tooltip describes. */
  children: React.ReactNode;
  /** Optional className for the inline-flex trigger wrapper. */
  className?: string;
  /** Surface archetype: 'glass' (light UI hint, default) or 'chart' (dark, over charts). */
  variant?: "glass" | "chart";
  /** Opt-in keyboard focusability. Default false so passive hints don't inject tab stops. */
  focusable?: boolean;
}

// Pre-measure estimates: used ONLY for the FIRST-paint flip guess before the card
// is in the DOM. useLayoutEffect then re-measures the real box and corrects it, so
// long joined-string content never flips the wrong way.
const EST_W = 280;
const EST_H = 72;
const CLOSE_DELAY = 250; // ms — matches MetricInfoIcon; lets the cursor bridge into the panel

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  className,
  variant = "glass",
  focusable = false,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [measured, setMeasured] = useState(false); // stays 0-opacity until the real box is measured

  // Position from the trigger rect. Width/height default to the pre-measure consts
  // but are overridden with the real card box once measured.
  const computePosition = useCallback((w = EST_W, h = EST_H) => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let top = rect.bottom + 8;
    let left = rect.left;
    if (top + h > window.innerHeight - 16) top = rect.top - h - 8; // flip above
    if (left + w > window.innerWidth - 16) left = rect.right - w; // right-align
    left = Math.max(8, left);
    top = Math.max(8, top);
    setPosition({ top, left });
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const show = useCallback(() => {
    clearCloseTimer();
    setMeasured(false); // hide until re-measured → no wrong-flip flash
    computePosition();
    setIsVisible(true);
  }, [computePosition, clearCloseTimer]);

  const hide = useCallback(() => {
    clearCloseTimer();
    setIsVisible(false);
  }, [clearCloseTimer]);

  // Delayed close so the cursor can travel from the trigger into the hoverable panel.
  const scheduleHide = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setIsVisible(false), CLOSE_DELAY);
  }, [clearCloseTimer]);

  // Measure the REAL card after mount, correct the flip, then reveal (opacity 0 → 1).
  useLayoutEffect(() => {
    if (!isVisible || !panelRef.current) return;
    const { offsetWidth, offsetHeight } = panelRef.current;
    computePosition(offsetWidth, offsetHeight);
    setMeasured(true);
  }, [isVisible, content, computePosition]);

  // While open: Escape closes; scroll repositions (or closes if the trigger detached).
  useEffect(() => {
    if (!isVisible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };
    const onScroll = () => {
      const el = triggerRef.current;
      if (!el || !el.isConnected) {
        setIsVisible(false); // row unmounted by realtime / detached → close
        return;
      }
      computePosition(
        panelRef.current?.offsetWidth ?? EST_W,
        panelRef.current?.offsetHeight ?? EST_H
      );
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true); // capture: catch nested scrollers too
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isVisible, computePosition, hide]);

  // Clear any pending close timer on unmount.
  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  // Null/empty content → no tooltip at all (defensive: consumers may pass conditional content).
  if (content == null || content === "") return <>{children}</>;

  const surfaceClass = variant === "chart" ? "tt-surface-chart" : "tt-surface";

  return (
    <span
      ref={triggerRef}
      className={className ? `inline-flex ${className}` : "inline-flex"}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      tabIndex={focusable ? 0 : undefined}
      onFocus={focusable ? show : undefined}
      onBlur={focusable ? hide : undefined}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            ref={panelRef}
            role="tooltip"
            // z-[9999] matches MetricTooltip. A few modal surfaces paint at
            // z-10000/10001 (HistoryPanel, ConfirmRestoreModal) and can cover a
            // tooltip triggered from inside them — those in-modal controls keep
            // native `title`. Do NOT globally raise past the modal fleet.
            className={`${surfaceClass} text-xs z-[9999] transition-opacity duration-150`}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              opacity: measured ? 1 : 0, // reveal only after the real box is measured
              pointerEvents: "auto", // hoverable bridge — panel keeps itself open
            }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleHide}
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  );
};

export default Tooltip;
